import { Input, Row, Col, message, Typography } from 'antd';
import { Button } from '../../../../../button';
import { PlusOutlined, MinusOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { HfuBand } from '../../../../../../@types/projects/types';
import { useSelected } from '../../../../../../providers/selectedContext';
import { useAppContext } from '../../../../../../providers/AppContext';
import { updateHfuBands } from '../../../../../../api/data';

type BandRow = { cutoff: string; color: string };
type FormValues = { bands: BandRow[] };

const DEFAULT_COLORS = [
  '#006100', '#000080', '#7030a0', '#ff0000', '#ed7d31', '#ffc000', '#00b050',
];

const HfuBandsForm = () => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [initialBands, setInitialBands] = useState<HfuBand[]>([]);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);

  const addForm = useForm<FormValues>({
    defaultValues: { bands: [{ cutoff: '', color: DEFAULT_COLORS[0] }] },
  });
  const { fields, append, remove } = useFieldArray({ control: addForm.control, name: 'bands' });
  useWatch({ control: addForm.control, name: 'bands' });

  const onSubmit = async (data: FormValues) => {
    if (!selectedInfo.project) return;
    setLoading(true);
    try {
      const payload = {
        project_uid: selectedInfo.project.uid,
        hfu_bands: data.bands.map((band, index) => ({
          cutoff: index === data.bands.length - 1 ? null : parseFloat(band.cutoff.replace(',', '.')) || null,
          color: band.color,
        })),
      };
      const response = await updateHfuBands(selectedInfo.project.uid, payload);
      setSelectedInfo({ project: response });
      upsertProject(response);
      addForm.reset({ bands: [{ cutoff: '', color: DEFAULT_COLORS[0] }] });
      message.success('Flow unit bands updated');
    } catch {
      message.error('Error updating flow unit bands');
    } finally {
      setLoading(false);
    }
  };

  const editForm = useForm<{ bands: HfuBand[] }>({ defaultValues: { bands: [] } });
  const { fields: editFields } = useFieldArray({
    control: editForm.control,
    name: 'bands',
    keyName: 'formId',
  });
  const watchedEditBands = useWatch({ control: editForm.control, name: 'bands' });

  useEffect(() => {
    const stored = selectedInfo.project?.correlation?.hfu_bands ?? [];
    editForm.reset({ bands: stored });
    setInitialBands(stored);
  }, [selectedInfo.project, editForm]);

  const isEditDirty = (index: number): boolean => {
    const current = watchedEditBands?.[index];
    const initial = initialBands[index];
    if (!current || !initial) return false;
    return String(current.cutoff) !== String(initial.cutoff) || current.color !== initial.color;
  };

  const handleSaveSingle = async (index: number) => {
    if (!selectedInfo.project) return;
    const updated = editForm.getValues('bands').map((band, bandIndex) => ({
      cutoff: bandIndex === editFields.length - 1 ? null : (typeof band.cutoff === 'number' ? band.cutoff : parseFloat(String(band.cutoff).replace(',', '.')) || null),
      color: band.color,
    }));
    setSavingIndex(index);
    try {
      const response = await updateHfuBands(selectedInfo.project.uid, {
        project_uid: selectedInfo.project.uid,
        hfu_bands: updated,
      });
      setSelectedInfo({ project: response });
      upsertProject(response);
      message.success('Band updated');
    } catch {
      message.error('Error updating band');
    } finally {
      setSavingIndex(null);
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <form onSubmit={addForm.handleSubmit(onSubmit)}>
        {fields.map((field, index) => {
          const isLast = index === fields.length - 1;
          return (
            <Row gutter={8} key={field.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Col span={4}>
                <small>{`HFU${index}`}</small>
              </Col>
              <Col span={14}>
                <Controller
                  name={`bands.${index}.cutoff`}
                  control={addForm.control}
                  render={({ field: controllerField }) => (
                    <Input
                      {...controllerField}
                      placeholder={isLast ? '∞ (last band)' : 'Cutoff (0–1)'}
                      disabled={isLast}
                      type="text"
                      inputMode="decimal"
                      size="small"
                    />
                  )}
                />
              </Col>
              <Col span={6} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Controller
                  name={`bands.${index}.color`}
                  control={addForm.control}
                  render={({ field: controllerField }) => (
                    <input
                      type="color"
                      {...controllerField}
                      value={controllerField.value || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                      style={{ width: 32, height: 24, padding: 1, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
                    />
                  )}
                />
              </Col>
            </Row>
          );
        })}

        <div style={{ display: 'flex', alignItems: 'center', marginTop: 12 }}>
          <Button
            variant="primary"
            size="sm"
            startIcon={<PlusOutlined />}
            onClick={() => append({ cutoff: '', color: DEFAULT_COLORS[fields.length % DEFAULT_COLORS.length] })}
          />
          <Button
            variant="ghost"
            size="sm"
            startIcon={<MinusOutlined />}
            style={{ marginLeft: 8 }}
            disabled={fields.length <= 1}
            onClick={() => fields.length > 1 && remove(fields.length - 1)}
          />
          <Button variant="primary" type="submit" loading={loading} style={{ marginLeft: 'auto' }}>
            Submit Bands
          </Button>
        </div>
      </form>

      <hr style={{ margin: '16px 0' }} />

      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Flow unit bands created ({editFields.length})</p>
        </Col>
      </Row>

      {editFields.length === 0 ? (
        <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
          Using default HFU bands. Submit new bands to override.
        </Typography.Text>
      ) : (
        editFields.map((field, index) => {
          const isLast = index === editFields.length - 1;
          return (
            <Row gutter={8} key={field.formId} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Col span={4}>
                <small>{field.label}</small>
              </Col>
              <Col span={13}>
                <Controller
                  name={`bands.${index}.cutoff`}
                  control={editForm.control}
                  render={({ field: controllerField }) => (
                    <Input
                      {...controllerField}
                      value={isLast ? '∞' : String(controllerField.value ?? '')}
                      placeholder="Cutoff (0–1)"
                      disabled={isLast}
                      type="text"
                      inputMode="decimal"
                      size="small"
                    />
                  )}
                />
              </Col>
              <Col span={7} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Controller
                  name={`bands.${index}.color`}
                  control={editForm.control}
                  render={({ field: controllerField }) => (
                    <input
                      type="color"
                      {...controllerField}
                      value={controllerField.value || '#000000'}
                      style={{ width: 32, height: 24, padding: 1, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                    />
                  )}
                />
                {isEditDirty(index) && (
                  <Button
                    variant="primary"
                    size="sm"
                    startIcon={<SaveOutlined />}
                    loading={savingIndex === index}
                    onClick={() => handleSaveSingle(index)}
                    style={{ flexShrink: 0 }}
                  />
                )}
              </Col>
            </Row>
          );
        })
      )}
    </div>
  );
};

export default HfuBandsForm;
